import { Grid, Typography } from "@material-ui/core";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "src/app-redux/hooks";
import { getAllMultiContainerGateDocuments } from "src/app-redux/vehicle/actions/vehicleActions";
import { selectVehicleDetails } from "src/app-redux/vehicle/vehicleSlice";
import { useStyles } from "./../style";
import IconButton from "@mui/material/IconButton";
import img from "src/assets/images/file_formats_logos/img.svg";
import doc from "src/assets/images/file_formats_logos/doc.svg";
import pdf from "src/assets/images/file_formats_logos/pdf.svg";
import xlsx from "src/assets/images/file_formats_logos/xlsx.svg";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { downloadFile } from "src/app-redux/common-actions";

export interface IDrawerDetailsProps {}

interface IDetailFieldConfig {
  label: string;
  getValue: (detail: any) => any;
  format?: (value: any) => React.ReactNode;
}

const getDisplayValue = (value: any, format?: (value: any) => React.ReactNode) =>
  format ? format(value) : value ? value : "-";

const isEmptyDetailValue = (value: any, format?: (value: any) => React.ReactNode) => {
  const displayValue = getDisplayValue(value, format);
  return (
    displayValue === "-" ||
    displayValue === null ||
    displayValue === undefined ||
    displayValue === ""
  );
};

const DetailFieldContent = ({
  label,
  value,
  format,
}: {
  label: string;
  value: any;
  format?: (value: any) => React.ReactNode;
}) => (
  <>
    <Typography className="subHeading">{label}</Typography>
    <Typography className="subHeadingDetails">{getDisplayValue(value, format)}</Typography>
  </>
);

const formatRemarks = (remarksText: string) => {
  const remarksArray = remarksText.split("@``@");
  return remarksArray?.map((value: any, index: number) => (
    <>
      <div key={index}>{value ? value : ""}</div>
      {index !== remarksArray.length - 1 && remarksArray[0].length > 1 && <br />}
    </>
  ));
};

interface IDetailRowConfig {
  left: IDetailFieldConfig;
  right?: IDetailFieldConfig;
}

const getVisibleFields = (rows: IDetailRowConfig[], detail: any) =>
  rows.flatMap((row) => {
    const fields: { config: IDetailFieldConfig; value: any }[] = [];
    const leftValue = row.left.getValue(detail);
    if (!isEmptyDetailValue(leftValue, row.left.format)) {
      fields.push({ config: row.left, value: leftValue });
    }
    if (row.right) {
      const rightValue = row.right.getValue(detail);
      if (!isEmptyDetailValue(rightValue, row.right.format)) {
        fields.push({ config: row.right, value: rightValue });
      }
    }
    return fields;
  });

const DetailFieldRow = ({
  leftField,
  rightField,
}: {
  leftField: { config: IDetailFieldConfig; value: any };
  rightField?: { config: IDetailFieldConfig; value: any };
}) => (
  <Grid item xs={12} className="itemRow">
    <Grid container direction="row">
      <Grid item xs={1}></Grid>
      <Grid item xs={rightField ? 6 : 11}>
        <DetailFieldContent
          label={leftField.config.label}
          value={leftField.value}
          format={leftField.config.format}
        />
      </Grid>
      {rightField && (
        <Grid item xs={5}>
          <DetailFieldContent
            label={rightField.config.label}
            value={rightField.value}
            format={rightField.config.format}
          />
        </Grid>
      )}
    </Grid>
  </Grid>
);

const DetailRow = ({ left, right, detail }: IDetailRowConfig & { detail: any }) => {
  const leftValue = left.getValue(detail);
  const rightValue = right?.getValue(detail);

  return (
    <DetailFieldRow
      leftField={{ config: left, value: leftValue }}
      rightField={right ? { config: right, value: rightValue } : undefined}
    />
  );
};

const DetailSection = ({
  rows,
  detail,
  hideEmptyFields,
}: {
  rows: IDetailRowConfig[];
  detail: any;
  hideEmptyFields: boolean;
}) => {
  if (!detail) return null;

  if (hideEmptyFields) {
    const visibleFields = getVisibleFields(rows, detail);
    const packedRows: { config: IDetailFieldConfig; value: any }[][] = [];

    for (let i = 0; i < visibleFields.length; i += 2) {
      packedRows.push(visibleFields.slice(i, i + 2));
    }

    return (
      <>
        {packedRows.map((pair, index) => (
          <DetailFieldRow key={index} leftField={pair[0]} rightField={pair[1]} />
        ))}
      </>
    );
  }

  return (
    <>
      {rows.map((row, index) => (
        <DetailRow key={index} left={row.left} right={row.right} detail={detail} />
      ))}
    </>
  );
};

const containerDetailRows: { left: IDetailFieldConfig; right: IDetailFieldConfig }[] = [
  {
    left: { label: "Container Number", getValue: (d) => d?.containerNumber },
    right: { label: "Container Seal Number", getValue: (d) => d?.sealNo },
  },
  {
    left: { label: "Bill Number", getValue: (d) => d?.billNo },
    right: { label: "VIR Number", getValue: (d) => d?.virNo },
  },
  {
    left: { label: "Good’s Declaration Number (GD#)", getValue: (d) => d?.gdNo },
    right: { label: "Vessel", getValue: (d) => d?.vessel },
  },
  {
    left: { label: "Container Size", getValue: (d) => d?.containerSize },
    right: { label: "Container Type", getValue: (d) => d?.containerType },
  },
  {
    left: { label: "Container Load Type", getValue: (d) => d?.containerLoadType },
    right: { label: "Movement", getValue: (d) => d?.movement },
  },
  {
    left: { label: "Ro Number", getValue: (d) => d?.roNumber },
    right: { label: "Activity", getValue: (d) => d?.activity },
  },
  {
    left: { label: "Cost Center", getValue: (d) => d?.costCenter },
    right: { label: "Sub Line", getValue: (d) => d?.subLine },
  },
  {
    left: { label: "Line", getValue: (d) => d?.line },
    right: { label: "Cash Client", getValue: (d) => d?.cashClient },
  },
  {
    left: { label: "Client", getValue: (d) => d?.client },
    right: { label: "Shipper", getValue: (d) => d?.shipper },
  },
  {
    left: { label: "LP/ Booking No", getValue: (d) => d?.bookingNumber },
    right: { label: "Transporter", getValue: (d) => d?.transporter },
  },
  {
    left: { label: "Consignee", getValue: (d) => d?.consignee },
    right: { label: "Previous Location", getValue: (d) => d?.previousLocation },
  },
  {
    left: { label: "C&F Agent", getValue: (d) => d?.cfAgent },
    right: { label: "Destination", getValue: (d) => d?.destination },
  },
  {
    left: { label: "Voyage", getValue: (d) => d?.voyage },
    right: { label: "Discharge Port", getValue: (d) => d?.dischargePort },
  },
  {
    left: { label: "Doc Ref Number", getValue: (d) => d?.docRefNumber },
    right: { label: "Shift", getValue: (d) => d?.shift },
  },
  {
    left: { label: "Pol", getValue: (d) => d?.pol },
    right: { label: "SCAC Code", getValue: (d) => d?.scacCode },
  },
  {
    left: {
      label: "Apply Storage",
      getValue: (d) => d?.applyStorage,
      format: (v) => (v ? "Yes" : "No"),
    },
    right: { label: "Charges Amount", getValue: (d) => d?.chargesAmount },
  },
  {
    left: { label: "Charges Status", getValue: (d) => d?.chargesStatus },
    right: { label: "Previous Remarks", getValue: (d) => d?.previousRemarks },
  },
  {
    left: { label: "EIR Remarks", getValue: (d) => d?.eirRemarks },
    right: { label: "Forwarder", getValue: (d) => d?.forwarder },
  },
  {
    left: { label: "Container Gross Weight", getValue: (d) => d?.containerGrossWeight },
    right: { label: "NLC Seal Number:", getValue: (d) => d?.nlcSealNo },
  },
];

const driverDetailRows: { left: IDetailFieldConfig; right?: IDetailFieldConfig }[] = [
  {
    left: { label: "Driver Name", getValue: (d) => d?.driverName },
    right: { label: "Driver’s Father Name", getValue: (d) => d?.driverFatherName },
  },
  {
    left: { label: "Driver’s Mobile Number", getValue: (d) => d?.driverMobileNumber },
    right: { label: "Driver’s CNIC", getValue: (d) => d?.driverCNIC },
  },
  {
    left: { label: "Travelling Doc Type", getValue: (d) => d?.travellingDocType },
    right: { label: "Travelling Doc Number", getValue: (d) => d?.travellingDocNumber },
  },
  {
    left: { label: "NTN", getValue: (d) => d?.driverNTN },
    right: { label: "Resident", getValue: (d) => d?.driverResident },
  },
];

const cargoDetailRows: { left: IDetailFieldConfig; right?: IDetailFieldConfig }[] = [
  {
    left: { label: "Type Of Consignment", getValue: (d) => d?.consignmentTypeName },
    right: { label: "Type Of Cargo", getValue: (d) => d?.cargoTypeName },
  },
  {
    left: { label: "Good’s Declaration Number (GD#)", getValue: (d) => d?.gdNo },
    right: {
      label: "GD Date",
      getValue: (d) => d?.gdDate,
      format: (v) => (v ? moment(new Date(v)).format("DD-MM-YYYY") : "-"),
    },
  },
  {
    left: { label: "Supplier Name", getValue: (d) => d?.supplierName },
    right: { label: "WeBOC ID (IGM#)", getValue: (d) => d?.webBocId },
  },
];

const othersDetailRows: { left: IDetailFieldConfig; right?: IDetailFieldConfig }[] = [
  {
    left: {
      label: "Entry Date and Time",
      getValue: (d) => d?.dateTime,
      format: (v) => (v ? moment(new Date(v)).format("DD-MM-YYYY hh:mm A") : "-"),
    },
    right: {
      label: "Gate-in Remarks",
      getValue: (d) => d?.notes,
      format: (notes) => (notes ? formatRemarks(notes) : "-"),
    },
  },
  {
    left: { label: "Customs Inspector Remarks", getValue: (d) => d?.remarks },
    right: { label: "Gate-out Remarks", getValue: (d) => d?.gateOutComments },
  },
  {
    left: { label: "WeBOC Vehicle Number", getValue: (d) => d?.webocVehicleNumber },
  },
];

export default function DrawerDetails(props: IDrawerDetailsProps) {
  const classes = useStyles();
  const terminalType = Number(localStorage.getItem("terminalType"));
  const hideEmptyFields = terminalType === 4;
  // Redux
  const vehicleDetailsResponse: any = useAppSelector(selectVehicleDetails);
  const dispatch = useDispatch();
  const [allDocs, setAllDocs] = useState<any[]>([]);
  const vehicleDetail = vehicleDetailsResponse?.vehicleDetail;

  useEffect(() => {
    vehicleDetailsResponse?.vehicleDetail?.vehicleId && getPictureGalleryContent();
  }, [vehicleDetailsResponse]);

  const getPictureGalleryContent = async () => {
    const res: any = await dispatch(
      getAllMultiContainerGateDocuments({
        gatePassId: vehicleDetailsResponse?.vehicleDetail?.vehicleId,
      })
    );
    if (res.payload) setAllDocs(res.payload);
  };

  const downloadDocument = async (item: any) => {
    const type =
      item.fileCategory === "Scan"
        ? 2
        : item.fileCategory === "Weight"
        ? 3
        : item.fileCategory === "Commercial"
        ? 4
        : 1;
    const fileMetaData = {
      url: `${process.env.REACT_APP_API_URL}/get-gate-doc-by-id/${item.id}?docType=${type}`,
      contentType: ".jpg",
      fileName: item.title,
    };
    dispatch(downloadFile(fileMetaData));
  };

  return (
    <div className={classes.drawerDetails}>
      {/* Container Details */}
      <Typography className="heading">Container Details </Typography>
      <Grid container spacing={2} className="topContainer">
        <DetailSection
          rows={containerDetailRows}
          detail={vehicleDetail}
          hideEmptyFields={hideEmptyFields}
        />
      </Grid>

      {/* Driver Details */}
      <Typography className="heading">Driver Details </Typography>
      <Grid container spacing={2} className="topContainer">
        <DetailSection
          rows={driverDetailRows}
          detail={vehicleDetail}
          hideEmptyFields={hideEmptyFields}
        />
      </Grid>

      {/* Cargo Information */}
      <Typography className="heading">Cargo Information </Typography>
      <Grid container spacing={2} className="topContainer">
        <DetailSection
          rows={cargoDetailRows}
          detail={vehicleDetail}
          hideEmptyFields={hideEmptyFields}
        />
      </Grid>

      {/* Others */}
      <Typography className="heading">Others </Typography>
      <Grid container spacing={2} className="topContainer">
        <DetailSection
          rows={othersDetailRows}
          detail={vehicleDetail}
          hideEmptyFields={hideEmptyFields}
        />
      </Grid>

      {/* Picture Gallery */}
      <Typography className="heading">All Documents </Typography>
      <Grid container spacing={2} className="topContainer">
        <Grid container direction="row" className="itemRow" spacing={2}>
          <Grid
            item
            md={12}
            id="main-div"
            className={classes.drawerDetails}
            style={{
              height: "max-content",
            }}
          >
            <Grid container direction="row">
              {/* History Item  */}
              {allDocs.length !== 0 ? (
                allDocs.map((item: any) => (
                  <Grid md={6} key={item.id} style={{ padding: "5px" }}>
                    <Grid container direction="row" className="itemRowTrail">
                      <Grid md={3} style={{ padding: "5px" }}>
                        <img
                          src={
                            item.extension === "doc" || item.extension === "docx"
                              ? doc
                              : item.extension === "jpg" ||
                                item.extension === "jpeg" ||
                                item.extension === "png"
                              ? img
                              : item.extension === "pdf"
                              ? pdf
                              : item.extension === "xlsx"
                              ? xlsx
                              : img
                          }
                          alt={item.extension}
                          width="25px"
                        />
                      </Grid>
                      <Grid md={6}>
                        <Typography className="subHeading itemName">
                          {item.originalFileName}
                        </Typography>
                      </Grid>
                      <Grid md={2}>
                        <IconButton
                          onClick={() => {
                            downloadDocument(item);
                          }}
                        >
                          <CloudDownloadIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                ))
              ) : (
                <div></div>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* <Grid container spacing={2} className="topContainer">
        <LightGallery onInit={onInit} speed={500} plugins={[lgThumbnail, lgZoom]}>
          {getItems()}
        </LightGallery>
      </Grid> */}
    </div>
  );
}
